/**
 * Block 12 PR-B Phase B3 Part C Step 20 — SectionLighting (Д).
 * Hotfix-2C Phase C4 Step 9 — collapsible single-row pattern per Schemas v2.
 *
 * **Header** (always visible): chevron-toggle button + label «Добавить подсветку».
 *
 * **Collapsed (default)** — только header. **Expanded** — header + 4 fixed rows
 * (LED-лента / Профиль 3 м / Сенсорный включатель / Трансформатор) с qty/price
 * NumberInputs + per-row жёлтая кнопка «Найти и узнать цену» → /furniture
 * (PR-calc-buttons-unify, ADR-0099, target amended ADR-0100; ранее была одна
 * общая серая кнопка в header).
 *
 * Prop `enabled` (исторически «master toggle on») теперь несёт семантику
 * «expanded». Reducer side-effect TOGGLE_LIGHTING.on=true сохраняется и
 * seed'ит `lightingQuantities` значениями `defaultQty=1` per методика §1.4.1
 * при первом раскрытии (existing custom values не перезаписываются).
 *
 * Source: Block_12_anketa_schemas_2026-05-17_v2.docx lines 52–58
 * (повторяется в 7 анкетах: Кухня, Шкаф, Bath-tabletop, Bath-shelf-above,
 * Bath-cabinet, ТВ-зона, Балкон).
 *
 * Reference: ADR-0020 §Q1, методика §1.4.1.
 */

import { ChevronRight } from 'lucide-react';
import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { ResultRow } from './ResultRow';
import type { ResultRowData } from '../types';
import { LIGHTING_ROW_SPECS } from '../helpers/lightingRowSpecs';

// Backward-compat re-export для существующих тестов и computeTotal.
const LIGHTING_ROWS = LIGHTING_ROW_SPECS;

export interface SectionLightingProps {
  /** Раскрыт ли раздел (4 rows visible). Reducer seed'ит quantities при первом true. */
  enabled: boolean;
  quantities: Record<string, number>;
  prices: Record<string, number>;
  onToggleEnabled: (on: boolean) => void;
  onQty: (id: string, qty: number) => void;
  onPrice: (id: string, price: number) => void;
  /** PR-calc-buttons-unify (ADR-0099, ADR-0100) — per-row жёлтая кнопка «Найти и узнать цену» → /furniture. */
  onDeeplink?: (rowId: string) => void | Promise<void>;
}

export function SectionLighting({
  enabled,
  quantities,
  prices,
  onToggleEnabled,
  onQty,
  onPrice,
  onDeeplink,
}: SectionLightingProps) {
  return (
    <SectionCard testId="section-lighting">
      <SectionHeader>Подсветка</SectionHeader>
      <div className="flex justify-between items-center gap-2">
        <button
          type="button"
          onClick={() => onToggleEnabled(!enabled)}
          aria-expanded={enabled}
          data-testid="lighting-expand-toggle"
          className="flex items-center gap-2 flex-1 min-h-tap text-left font-semibold text-ink focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none rounded-xl"
        >
          <ChevronRight
            size={20}
            className={`shrink-0 text-ghost transition-transform ${enabled ? 'rotate-90' : ''}`}
            aria-hidden="true"
          />
          <span>Добавить подсветку</span>
        </button>
      </div>
      {enabled ? (
        <div className="flex flex-col gap-3 mt-3 pl-7">
          {LIGHTING_ROWS.map((spec) => {
            const qty = quantities[spec.id] ?? 0;
            const price = prices[spec.id] ?? 0;
            const row: ResultRowData = {
              id: spec.id,
              name: spec.label,
              qty,
              qtyAuto: 0,
              qtyOverridden: false,
              unit: spec.unit,
              price,
              cost: qty * price,
              deeplink: 'furniture',
            };
            return (
              <ResultRow
                key={spec.id}
                row={row}
                onQtyChange={(q) => onQty(spec.id, q)}
                onPriceChange={(p) => onPrice(spec.id, p)}
                onDeeplink={onDeeplink ? () => onDeeplink(spec.id) : undefined}
              />
            );
          })}
        </div>
      ) : null}
    </SectionCard>
  );
}

export { LIGHTING_ROWS };
